import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Calendar,
  Check,
  ChevronDown,
  Globe,
  Lock,
  MapPin,
  Save,
  Timer,
  Undo2,
  X,
} from "lucide-react";
import {
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import {
  DURATION_SETTINGS,
  SEASON_SETTINGS,
  TAG_LIST,
  TRAIL_LIST,
  CURRENCY_SETTINGS,
} from "../constants/packSettings";
import CancelIcon from "@mui/icons-material/Cancel";
import { useMediaQuery } from "react-responsive";

const getDisplayChips = (tags, isMobile) => {
  const { duration, season, trail } = tags;
  return [
    trail && trail.length ? (
      <Chip
        key={trail}
        icon={<MapPin size={14} />}
        color="info"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={trail}
        sx={{ maxWidth: "180px", overflowX: "ellipsis", whitespace: "nowrap" }}
      />
    ) : null,
    season && season.length ? (
      <Chip
        key={season}
        icon={<Calendar size={14} />}
        color="warning"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={SEASON_SETTINGS[season]}
      />
    ) : null,
    duration && duration.length ? (
      <Chip
        key={duration}
        icon={<Timer size={14} />}
        color="secondary"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={DURATION_SETTINGS[duration]}
      />
    ) : null,
  ].filter((chip) => chip);
};

const getGenericChips = (tags, isMobile) => {
  const { tag0, tag1, tag2 } = tags;
  return [tag0, tag1, tag2]
    .map((tag) => {
      if (tag) {
        return (
          <Chip
            key={tag}
            size={isMobile ? "large" : "small"}
            color="success"
            variant="outlined"
            label={tag}
          />
        );
      } else {
        return null;
      }
    })
    .filter((chip) => chip);
};

const getDefaultTags = (userTags) => {
  const tagMap = userTags.reduce(
    (acc, { type, value }) => {
      acc[type] = value;
      return acc;
    },
    {
      duration: "",
      season: "",
      trail: "",
      tag0: "",
      tag1: "",
      tag2: "",
      hidePrices: false,
    }
  );
  return tagMap;
};

const getInitialFormat = (userTags) => {
  return Object.keys(userTags).map((key) => ({
    type: key,
    value: userTags[key],
  }));
};

const PackSettings = ({
  userPackTags,
  visibility,
  handleUpdatePackSettings,
  packAnalytics,
  packCurrency,
}) => {
  const userPackTagsStringified = JSON.stringify(userPackTags);
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [expanded, setExpanded] = React.useState(false);
  const [packIsPublic, setPackIsPublic] = React.useState(visibility);
  const [tags, setTags] = React.useState(getDefaultTags(userPackTags));
  const [genericTags, setGenericTags] = React.useState(
    [tags.tag0, tags.tag1, tags.tag2].filter((tag) => tag && tag.length > 0)
  );
  const [currency, setCurrency] = React.useState(packCurrency);
  const [isSaved, setIsSaved] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    setPackIsPublic(Boolean(visibility));
    setTags(getDefaultTags(userPackTags));
    const defaultTags = getDefaultTags(userPackTags);
    setGenericTags(
      [defaultTags.tag0, defaultTags.tag1, defaultTags.tag2].filter(
        (tag) => tag && tag.length > 0
      )
    );
  }, [userPackTagsStringified, userPackTags, visibility]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleMakePublic = () => {
    checkIfChangesExist(tags, !packIsPublic, currency);
    setPackIsPublic(!packIsPublic);
    setIsSaved(false);
  };

  const handleTagChange = (key, value) => {
    const valueOfTag = value === "NONE" || value == null ? "" : value;
    const newTags = { ...tags };
    newTags[key] = valueOfTag;
    checkIfChangesExist(newTags, packIsPublic, currency);
    setTags(newTags);
    setIsSaved(false);
  };

  const handleUpdateCurrency = (newCurrency) => {
    checkIfChangesExist(tags, packIsPublic, newCurrency);
    setCurrency(newCurrency);
    setIsSaved(false);
  };

  const handleSave = () => {
    const tagsInInitialFormat = getInitialFormat(tags);
    handleUpdatePackSettings(packIsPublic, tagsInInitialFormat, currency);
    setIsSaved(true);
    setHasChanges(false);
  };

  const checkIfChangesExist = (newTags, privacySetting, newCurrency) => {
    const hasVisibilityChanged = privacySetting !== visibility;
    const changedTags = userPackTags.filter(({ type, value }) => {
      return value !== newTags[type];
    });
    const hasTagsChanged = changedTags.length > 0;
    const hasCurrencyChanged = newCurrency !== packCurrency;
    const hasChanged =
      hasVisibilityChanged || hasTagsChanged || hasCurrencyChanged;
    setHasChanges(hasChanged);
  };

  const handleResetTags = () => {
    const resetTags = getDefaultTags(userPackTags);
    const resetGenericTags = [
      resetTags.tag0,
      resetTags.tag1,
      resetTags.tag2,
    ].filter((tag) => tag && tag.length > 0);
    setGenericTags(resetGenericTags);
    setTags(resetTags);
    setPackIsPublic(visibility);
    setCurrency(packCurrency);
    setIsSaved(false);
    setHasChanges(false);
  };

  const handleSetGenericTag = (selectedTags) => {
    const newUserTags = tags;
    for (let i = 0; i < 3; i++) {
      if (i >= selectedTags.length) {
        newUserTags[`tag${i}`] = "";
      } else {
        newUserTags[`tag${i}`] = selectedTags[i];
      }
    }
    setTags(newUserTags);
    checkIfChangesExist(newUserTags, packIsPublic);
    setGenericTags(selectedTags);
    setIsSaved(false);
  };

  const handleDeleteChip = (value) => {
    const newTagList = genericTags.filter((v) => v !== value);
    handleSetGenericTag(newTagList);
  };

  const formattedViewHistory = {
    labels: packAnalytics.viewHistory
      ? packAnalytics.viewHistory.map(({ date }) => date)
      : [],
    datasets: [
      {
        label: "Views",
        data: packAnalytics.viewHistory
          ? packAnalytics.viewHistory.map(({ views }) => views)
          : [],
        borderColor: "rgb(53, 162, 235)",
      },
    ],
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          marginTop: "10px !important",
          height: isMobile ? "50px" : "40px",
          width: "100%",
        }}
        spacing={1}
      >
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              maxWidth: isMobile ? "650px" : "100%",
              overflowX: "scroll",
            }}
          >
            <Chip
              label={packIsPublic ? "Public" : "Private"}
              icon={
                packIsPublic ? <Globe size={14} /> : <Lock size={14} />
              }
              color={packIsPublic ? "primary" : "error"}
              variant="outlined"
              size={isMobile ? "large" : "small"}
              sx={{ width: isMobile ? "105px" : "85px" }}
            />
            {getDisplayChips(tags, isMobile)}
            {getGenericChips(tags, isMobile)}
          </Stack>
          <Button
            size="small"
            endIcon={<ChevronDown size={16} />}
            onClick={handleExpandClick}
            sx={{
              height: isMobile ? "30px" : "22px",
              borderRadius: "9px",
              fontFamily: HEADING_FONT,
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            Settings & Analytics
          </Button>
        </Stack>
      </Stack>
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{ width: "100%" }}
      >
        <Paper
          elevation={0}
          sx={(theme) => ({
            marginTop: "5px",
            borderRadius: "12px",
            border: `1px solid ${getBorder(theme.palette.mode === "dark")}`,
            backgroundImage: "none",
          })}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            paddingRight="10px"
          >
            <Stack>
              <Stack spacing={0}>
                <List sx={{ paddingBottom: "0px", paddingTop: "0px" }}>
                  <ListItem key="private-checkbox" sx={{ width: "220px" }}>
                    <Tooltip title='Public packs show up in the global "Browse Packs" page. Private packs are only visible to you and anyone you share your link with.'>
                      <ListItemButton
                        role={undefined}
                        onClick={handleMakePublic}
                        dense
                      >
                        <ListItemIcon sx={{ marginLeft: "-15px" }}>
                          <Checkbox
                            icon={<Lock size={20} />}
                            checkedIcon={<Globe size={20} />}
                            checked={packIsPublic}
                          />
                        </ListItemIcon>
                        <ListItemText
                          sx={{ marginLeft: "-10px" }}
                          primary={`Visibility: ${
                            packIsPublic ? "Public" : "Private"
                          }`}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                </List>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ paddingLeft: "17px", paddingTop: "5px" }}
                >
                  <FormControl sx={{ width: "200px" }} size="small">
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={tags.duration}
                      label="Duration"
                      onChange={(e) => {
                        handleTagChange("duration", e.target.value);
                      }}
                    >
                      {Object.keys(DURATION_SETTINGS).map((key) => (
                        <MenuItem value={key} key={key}>
                          {DURATION_SETTINGS[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: "200px" }} size="small">
                    <InputLabel>Season</InputLabel>
                    <Select
                      value={tags.season}
                      label="Season"
                      onChange={(e) => {
                        handleTagChange("season", e.target.value);
                      }}
                    >
                      {Object.keys(SEASON_SETTINGS).map((key) => (
                        <MenuItem value={key} key={key}>
                          {SEASON_SETTINGS[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
              <Autocomplete
                size="small"
                sx={{ width: "410px", marginLeft: "17px", marginTop: "15px" }}
                freeSolo
                options={TRAIL_LIST.map((option) => option)}
                value={tags.trail}
                onChange={(e, value) => {
                  handleTagChange("trail", value);
                }}
                onInputChange={(e, value) => {
                  handleTagChange("trail", value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps, maxLength: 36 }}
                    label="Trail or Location"
                  />
                )}
              />
              <FormControl sx={{ marginLeft: "17px", marginTop: "15px" }}>
                <InputLabel size="small">{"Tags (Max 3)"}</InputLabel>
                <Select
                  multiple
                  value={genericTags}
                  onChange={(e) => {
                    handleSetGenericTag(e.target.value);
                  }}
                  on
                  input={<OutlinedInput label="Tags (Max 3)" />}
                  size="small"
                  sx={{ width: "410px" }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          clickable
                          onDelete={() => {
                            handleDeleteChip(value);
                          }}
                          deleteIcon={
                            <CancelIcon
                              onMouseDown={(event) => event.stopPropagation()}
                            />
                          }
                        />
                      ))}
                    </Box>
                  )}
                >
                  {TAG_LIST.map((tag) => (
                    <MenuItem
                      key={tag}
                      value={tag}
                      disabled={
                        !genericTags.includes(tag) && genericTags.length >= 3
                      }
                      sx={{ width: "400px" }}
                    >
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row">
                <Autocomplete
                  size="small"
                  sx={{
                    width: "200px",
                    marginLeft: "17px",
                    marginTop: "15px",
                  }}
                  getOptionLabel={(option) => {
                    try {
                      return `${option} - ${new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: option,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(1)}`;
                    } catch (e) {
                      return option;
                    }
                  }}
                  options={CURRENCY_SETTINGS.map((option) => option)}
                  value={currency}
                  onChange={(e, value) => {
                    handleUpdateCurrency(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, maxLength: 36 }}
                      label="Currency"
                    />
                  )}
                />
                <FormGroup>
                  <Tooltip title="Check this to hide prices when sharing your pack.">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tags.hidePrices}
                          onChange={(e) => {
                            handleTagChange("hidePrices", e.target.checked);
                          }}
                        />
                      }
                      label="Hide Prices"
                      sx={{ marginLeft: "17px", marginTop: "15px" }}
                    />
                  </Tooltip>
                </FormGroup>
              </Stack>
              <Stack direction="row" spacing={0} sx={{ marginBottom: "10px" }}>
                {isSaved ? (
                  <Button
                    sx={{
                      width: "100px",
                      marginLeft: "17px",
                      marginTop: "15px",
                      borderRadius: "9px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                    startIcon={<Check size={16} />}
                    color="success"
                    variant="outlined"
                    disabled
                  >
                    Saved
                  </Button>
                ) : (
                  <Button
                    sx={{
                      width: "100px",
                      marginLeft: "17px",
                      marginTop: "15px",
                      borderRadius: "9px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                    startIcon={<Save size={16} />}
                    color="success"
                    variant="outlined"
                    onClick={handleSave}
                    disabled={!hasChanges}
                  >
                    Save
                  </Button>
                )}
                <Button
                  sx={{
                    width: "180px",
                    marginLeft: "17px",
                    marginTop: "15px",
                    borderRadius: "9px",
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                  startIcon={<Undo2 size={16} />}
                  onClick={handleResetTags}
                  disabled={!hasChanges}
                >
                  Discard Changes
                </Button>
              </Stack>
              {hasChanges ? (
                <Typography
                  color="error"
                  variant="subtitle2"
                  sx={{ marginLeft: "17px" }}
                >
                  You have unsaved changes
                </Typography>
              ) : (
                <></>
              )}
            </Stack>
            <Stack
              sx={{
                paddingTop: "5px",
                paddingBottom: "10px",
                width: "100%",
              }}
              alignItems="flex-end"
            >
              <IconButton onClick={handleExpandClick}>
                <X size={18} />
              </IconButton>
              <Paper
                sx={(theme) => ({
                  padding: "14px",
                  height: "100%",
                  width: "100%",
                  borderRadius: "12px",
                  border: `1px solid ${getBorder(
                    theme.palette.mode === "dark"
                  )}`,
                  backgroundImage: "none",
                })}
                elevation={0}
              >
                <Stack direction="row" spacing={2}>
                  {[
                    { value: packAnalytics.views, label: "Views" },
                    {
                      value:
                        packAnalytics.upvoteCount - packAnalytics.downvoteCount,
                      label: "Votes",
                    },
                    { value: packAnalytics.upvoteCount, label: "Upvotes" },
                  ].map(({ value, label }) => (
                    <Paper
                      key={label}
                      elevation={0}
                      sx={(theme) => ({
                        padding: "8px 16px",
                        borderRadius: "10px",
                        border: `1px solid ${getBorder(
                          theme.palette.mode === "dark"
                        )}`,
                        backgroundImage: "none",
                      })}
                    >
                      <Stack alignItems="center">
                        <Typography
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 700,
                            fontSize: "18px",
                          }}
                        >
                          {value}
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontFamily: HEADING_FONT,
                            fontWeight: 600,
                            fontSize: "10.5px",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: getMutedText(
                              theme.palette.mode === "dark"
                            ),
                          })}
                        >
                          {label}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Stack>
                  <Typography
                    sx={(theme) => ({
                      paddingTop: "12px",
                      textAlign: "center",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: getMutedText(theme.palette.mode === "dark"),
                    })}
                  >
                    Last 30 days
                  </Typography>
                  <Line
                    style={{ maxHeight: "220px" }}
                    data={formattedViewHistory}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>
    </>
  );
};

export default PackSettings;
